import React, { useEffect, useRef, useState } from 'react';
import { Animated, ImageBackground, Linking, View } from 'react-native';

import B from '../../../components/Bold';
import { Button, Text, TextInput, useTheme } from 'react-native-paper';
import { NavigationWithProps } from '../../../core/Types';
import { LoginNavParamList } from '../Login';
import { useToast } from 'react-native-toast-notifications';
import { useAuth } from '../../../core/api/auth';
import { hasLengthBetween, isEmpty, isUndefinedOrNull, isValidPassword } from '../../../core/forms/forms';
import TextFormField from '../../../components/TextFormField';

const PasswordRestoreStep: React.FC<NavigationWithProps<{}, LoginNavParamList, "PasswordRestore">> = ({ navigation, route }) => {

    const screenOpacityAnim = useRef(new Animated.Value(0)).current;

    const formTranslateAnim = useRef(new Animated.Value(30)).current;
    const formOpacityAnim = useRef(new Animated.Value(0)).current;

    const { therapistId, clinicId } = route.params || {};

    const theme = useTheme();
    const auth = useAuth();
    const toast = useToast();

    const [resendCountDown, setResendCountDown] = useState<number>(59);
    const [loading, setLoading] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(true);
    const [codeRequested, setCodeRequested] = useState<boolean>(false);

    const [email,] = useState<string>(route.params.email);
    const [password, setPassword] = useState<string>("");

    const [verifCode, setVerifCode] = useState<string>("");
    const [token, setToken] = useState<string>("");

    const validatePassword = (password: string): string | undefined => {
        if (isUndefinedOrNull(password) || isEmpty(password)) {
            return "La contraseña es obligatoria";
        }

        if (!hasLengthBetween(password, 8, 100)) {
            return "La contraseña debe tener entre 8 y 100 caracteres";
        }

        if (!isValidPassword(password)) {
            return "Una mayúscula, una minúscula, un número y un carácter especial";
        }
    };

    const resetPassword = async () => {
        setLoading(true);

        if (validatePassword(password) || !verifCode || !token) {
            onError();
            setLoading(false);
            return;
        }

        auth.confirmResetPassword({ email, password, verification_code: verifCode, verification_token: token }).then(() => {
            toast.show("Contraseña restablecida exitosamente", { duration: 1000 });
            setTimeout(() => {
                navigation.replace("LoginEmail", { email, therapistId, clinicId });
            }, 1000);
        }).catch(() => {
            onError();
        }).finally(() => {
            setLoading(false);
        });

    };

    const resendVerificationCode = () => {
        setLoading(true);
        auth.emailVerificationForPasswordReset(email)
            .then((verificationToken: string) => {
                toast.show("Código de verificación reenviado", { duration: 1000 });
                setVerifCode("");
                setToken(verificationToken);
                initCountdown(59);
            })
            .catch((e) => {
                console.error("Error during email verification:", e);
                toast.show("Error al enviar el código de verificación", { duration: 1000 });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const requestCode = () => {
        initCountdown(59);
        resendVerificationCode();

        setCodeRequested(true);
        Animated.timing(formTranslateAnim, {
            toValue: 0,
            duration: 500,
            useNativeDriver: true,
        }).start();
        Animated.timing(formOpacityAnim, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
        }).start();
    };

    const initCountdown = (seconds: number) => {
        setResendCountDown(seconds);

        const interval = setInterval(() => {
            setResendCountDown((prev) => {
                if (prev <= 0) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        setTimeout(() => {
            return () => clearInterval(interval);
        }, 60000);
    }

    const onError = () => {
        console.log('Error:', 'Error');
        toast.show("Email o contraseña incorrectos", { duration: 1000 });
    };

    useEffect(() => {
        Animated.timing(screenOpacityAnim, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
        }).start();
    }, []);

    return (
        <Animated.View style={{ height: "100%", opacity: screenOpacityAnim }}>
            <ImageBackground
                style={{
                    height: "100%",
                    flexDirection: 'column',
                }}
                source={require("../../../../assets/img/scenes/fondo_marco.png")}>

                <View style={{
                    marginTop: 10,
                }}>
                    <Button
                        style={{ width: 100, marginLeft: 10 }}
                        icon="arrow-left"
                        textColor={theme.colors.primary}
                        onPress={() => navigation.goBack()}>
                        Volver
                    </Button>
                </View>

                <View style={{
                    marginTop: 100,
                }}>
                    <Text style={{
                        fontSize: 20,
                        textAlign: 'center',
                        marginBottom: 10,
                        color: theme.colors.onTertiaryContainer,
                    }}>
                        <B>Recuperar contraseña</B>
                    </Text>
                    <View style={{
                        marginHorizontal: 20,
                        marginBottom: 10
                    }}>
                        <TextFormField
                            label="Email"
                            value={email}
                            editable={false}
                            mode="outlined"
                        />
                        <Animated.View style={{
                            display: codeRequested ? 'flex' : 'none',
                            transform: [{ translateY: formTranslateAnim }],
                            opacity: formOpacityAnim
                        }}>
                            <TextFormField
                                label="Nueva contraseña"
                                value={password}
                                secureTextEntry={true}
                                onChangeText={setPassword}
                                validators={[validatePassword]}
                                setHasError={setHasError}
                                mode="outlined"
                            />
                            <View>
                                <TextInput
                                    label="Código de verificación"
                                    value={verifCode}
                                    onChangeText={setVerifCode}
                                    mode="outlined"
                                />
                                <Text style={{ marginTop: 5, marginBottom: 10, textAlign: 'right' }}>
                                    {resendCountDown > 0 ?
                                        <>Reenviar código en <Text style={{ color: theme.colors.primary }}> 00:{resendCountDown.toString().padStart(2, '0')} </Text></>
                                        : <Button mode='text' onPress={resendVerificationCode}>Reenviar código</Button>}
                                </Text>
                            </View>
                        </Animated.View>
                        {!codeRequested ?
                            <Button
                                style={{ borderRadius: 5 }}
                                mode="contained"
                                onPress={requestCode}
                                loading={loading}
                            >
                                Solicitar código
                            </Button> :
                            <Button
                                style={{ borderRadius: 5 }}
                                mode="contained"
                                onPress={resetPassword}
                                loading={loading}
                                disabled={hasError || !password || !verifCode}
                            >
                                Restablecer contraseña
                            </Button>
                        }
                    </View>
                </View>
            </ImageBackground>
        </Animated.View >
    );
}

export default PasswordRestoreStep;
